import React from 'react';
import { ScrollView, StyleProp, View, ViewStyle } from 'react-native';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  partStatistic,
  productionOrdersStatistic,
  productionOperationsStatistic,
  toolsStatistic,
  documentsStatistic,
  setStatStorage,
  setStatContextStorage,
} from '../../../../reducers/search';
import { LEVELS, withERP } from '@helpers';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts/index';

import Tile from './tile';
import NcFileStatusFilters from '@components/search/components/NcFileStatusFilters';
import { parseFacetsToStorage, formatODataFilter, calculateElementWidth } from './helpers';

import {
  PARTS,
  OPERATIONS,
  PRODUCTION_BATCHES,
  PRODUCTION_ORDERS,
  NC_FILES,
  TOOLS,
  IMAGES_DOCUMENTS,
  PRODUCTION_BATCHES_WITHOUT_ERP,
} from './config';
import { SCREEN_PADDING_HORIZONTAL, TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface ConfigReturn {
  name: string;
  icon: {
    name: string;
    color: string;
  };
  params: {
    oDataFilter: string;
  };
  value: any;
}

interface StatisticProps {
  C: Function;
  partStatistic: () => Promise<any>;
  productionOrdersStatistic: () => Promise<any>;
  productionOperationsStatistic: () => Promise<any>;
  productionBatchesStatistic: () => Promise<any>;
  ncFilesStatistic: () => Promise<any>;
  toolsStatistic: () => Promise<any>;
  documentsStatistic: () => Promise<any>;
  level: keyof typeof LEVELS;
  oDataFilter?: string;
  searchValue?: string;
  isSearchTriggered?: any;
  statisticTileName: string;
  setStatisticTileName: (name: string) => void;
  setStatStorage: ({ name, value }: { name: string; value: number }) => void;
  setStatContextStorage: (name: string) => void;
  search: (params: any) => Promise<void>;
  facets: any;
  loaded: boolean;
  style?: StyleProp<ViewStyle>;
  containerMarginBottom?: number;
  isWithERP: boolean | null;
  isPopupMode?: boolean;
  popupWidth?: number;
}

const Statistic: React.FC<StatisticProps> = ({
  scale = initialScale,
  font = initialFont,
  C,
  partStatistic,
  productionOrdersStatistic,
  productionOperationsStatistic,
  toolsStatistic,
  documentsStatistic,
  level,
  oDataFilter,
  searchValue,
  isSearchTriggered,
  statisticTileName,
  setStatisticTileName,
  search,
  facets,
  loaded: facetsLoaded,
  style,
  containerMarginBottom,
  isWithERP,
  isPopupMode,
  popupWidth,
  setStatStorage,
  setStatContextStorage,
}) => {
  const timeNow = moment.utc().format();
  const todayStartDate = moment.utc().startOf('day').format();
  const todayEndDate = moment.utc().add(1, 'day').startOf('day').format();
  const weekStart = moment.utc().startOf('isoWeek').format();
  const weekEnd = moment.utc().add(1, 'week').startOf('isoWeek').format();
  const nextWeekStart = moment.utc().add(1, 'week').startOf('isoWeek').format();
  const nextWeekEnd = moment.utc().add(2, 'week').startOf('isoWeek').format();

  const st = parseFacetsToStorage(facets);

  const config = {
    partStatistic,
    productionOrdersStatistic,
    productionOperationsStatistic,
    toolsStatistic,
    documentsStatistic,
    weekStart,
    weekEnd,
    nextWeekStart,
    nextWeekEnd,
    todayStartDate,
    timeNow,
    todayEndDate,
    facetsStorage: st || {},
    facets: facets || {},
    oDataFilter: formatODataFilter(oDataFilter),
    searchValue,
  };

  const storage = {
    [LEVELS.ALL]: [],
    [LEVELS.Parts]: PARTS(config),
    [LEVELS.Operations]: OPERATIONS(config),
    [LEVELS.ProductionBatches]: withERP(isWithERP) ? PRODUCTION_BATCHES(config) : PRODUCTION_BATCHES_WITHOUT_ERP(config),
    [LEVELS.ProductionOrders]: PRODUCTION_ORDERS(config),
    [LEVELS.NCFiles]: NC_FILES(config),
    [LEVELS.Tools]: TOOLS(config),
    [LEVELS.ImagesDocuments]: IMAGES_DOCUMENTS(config),
  };

  if (!storage[level]?.length) {
    return null;
  }

  if (level === LEVELS.NCFiles) {
    return (
      <NcFileStatusFilters
        facets={facets}
        facetsLoaded={facetsLoaded}
        selectedFilter={statisticTileName}
        onFilterChange={(filterName, filterODataFilter) => {
          setStatisticTileName(filterName || '');
          search({ level, oDataFilter: filterODataFilter || '', resetHistory: true });
        }}
        C={C}
        scale={scale}
        font={font}
        style={style}
        containerMarginBottom={containerMarginBottom}
        availableWidth={isPopupMode && popupWidth ? popupWidth : undefined}
        oDataFilter={oDataFilter}
      />
    );
  }

  const amountElements = storage[level].length;
  const gap = scale(9);
  const elementWidth = calculateElementWidth({ amountElements, gap, style, isPopupMode, popupWidth, scale });

  const tileTestIDs: Record<string, string> = {
    'Unknown NC File': TEST_IDS.searchScreen.statisticTiles.unknownNcFile,
    'Not Linked': TEST_IDS.searchScreen.statisticTiles.notLinked,
    'Single Link': TEST_IDS.searchScreen.statisticTiles.singleLink,
    'Multi Link': TEST_IDS.searchScreen.statisticTiles.multiLink,
    'Linked NC File': TEST_IDS.searchScreen.statisticTiles.linkedNcFile,
    Late: TEST_IDS.searchScreen.statisticTiles.late,
    'Now - Weekend': TEST_IDS.searchScreen.statisticTiles.nowWeekend,
    'Next Week': TEST_IDS.searchScreen.statisticTiles.nextWeek,
    'After Next Week': TEST_IDS.searchScreen.statisticTiles.afterNextWeek,
    'Finished This Week': TEST_IDS.searchScreen.statisticTiles.finishedThisWeek,
    'This Week': TEST_IDS.searchScreen.statisticTiles.thisWeek,
    Unfinished: TEST_IDS.searchScreen.statisticTiles.unfinished,
    'Finished Today': TEST_IDS.searchScreen.statisticTiles.finishedToday,
    'Modified this week': TEST_IDS.searchScreen.statisticTiles.modifiedThisWeek,
    'Created This Week': TEST_IDS.searchScreen.statisticTiles.createdThisWeek,
    'Part Images': TEST_IDS.searchScreen.statisticTiles.partImages,
    '2D Drawings': TEST_IDS.searchScreen.statisticTiles.twoDDrawings,
    'Not Attached': TEST_IDS.searchScreen.statisticTiles.notAttached,
  };

  return (
    <View testID={TEST_IDS.searchScreen.statisticContainer} style={{ marginBottom: containerMarginBottom ? containerMarginBottom : scale(18) }}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View
          style={[
            {
              flexDirection: 'row',
              gap: scale(9),
              paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
              justifyContent: 'space-between',
            },
            style,
            { width: 'auto' },
          ]}
        >
          {storage[level].map((i: any) => (
            <Tile
              {...{
                ...i,
                testID: tileTestIDs[i.name],
                C,
                level,
                useFacet: !i.api,
                facetsLoaded,
                elementWidth,
                isSearchTriggered,
                statisticTileName,
                setStatisticTileName,
                search,
                oDataFilter,
                font,
                scale,
                setStatStorage,
                setStatContextStorage,
              }}
              key={i.name}
            />
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

export default connect(({ membership: { isWithERP } }) => ({ isWithERP }), {
  partStatistic,
  productionOrdersStatistic,
  productionOperationsStatistic,
  toolsStatistic,
  documentsStatistic,
  setStatStorage,
  setStatContextStorage,
})(Statistic);
